import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useModule } from "@/hooks/use-module";
import { WaitlistCreate, WaitlistEntry, WaitlistStatus, WaitlistUpdate } from "@/types/waitlist";
import { requestData } from "@/api/api-config";
import { previewWaitlistMessage, sendWaitlistMessage, testSendWaitlistMessage, uploadWaitlistMessageFiles } from "@/api/provider/waitlist-messages";
export function usePreviewWaitlistMessage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: previewWaitlistMessage
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
export function useSendWaitlistMessage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: sendWaitlistMessage
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
export function useTestSendWaitlistMessage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: testSendWaitlistMessage
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
export function useUploadWaitlistMessageFiles() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}

// --- API functions ---
function _temp(t0) {
  const {
    files
  } = t0;
  return uploadWaitlistMessageFiles(files);
}
async function listWaitlistEntries(providerIds?: string[], desiredDate?: string, status?: WaitlistStatus[], patientId?: string, locationIds?: string[], matchProviderIds?: string[]): Promise<WaitlistEntry[] | null> {
  return requestData<WaitlistEntry[]>("waitlist", "get", {
    searchParams: {
      providerIds,
      desiredDate,
      status,
      patientId,
      locationIds,
      matchProviderIds
    }
  }, false);
}
async function createWaitlistEntry(data: WaitlistCreate): Promise<WaitlistEntry | null> {
  return requestData<WaitlistEntry>("waitlist", "post", {
    json: data
  }, false);
}
async function updateWaitlistEntry(waitlistId: string, data: WaitlistUpdate): Promise<WaitlistEntry | null> {
  return requestData<WaitlistEntry>(`waitlist/${waitlistId}`, "patch", {
    json: data
  }, false);
}
async function deleteWaitlistEntry(waitlistId: string): Promise<void> {
  await requestData(`waitlist/${waitlistId}`, "delete", {}, false);
}

// --- Query keys ---

export const waitlistQueryKeys = {
  all: ["waitlist"] as const,
  lists: () => [...waitlistQueryKeys.all, "list"] as const,
  detail: (id: string) => [...waitlistQueryKeys.all, "detail", id] as const,
  list: (providerIds?: string[], desiredDate?: string, status?: WaitlistStatus[], patientId?: string, locationIds?: string[], matchProviderIds?: string[]) => [...waitlistQueryKeys.lists(), {
    providerIds,
    desiredDate,
    status,
    patientId,
    locationIds,
    matchProviderIds
  }] as const
};
const activeWaitlistStatuses = [WaitlistStatus.WAITING];

// --- Query hooks ---

export function useWaitlistDetailQuery(waitlistId) {
  const $ = _c(8);
  const hasWaitlist = useModule("WAITLIST");
  let t0;
  if ($[0] !== waitlistId) {
    t0 = waitlistQueryKeys.detail(waitlistId ?? "");
    $[0] = waitlistId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== waitlistId) {
    t1 = () => requestData(`waitlist/${waitlistId}`, "get", {}, false);
    $[2] = waitlistId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = hasWaitlist && !!waitlistId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function useWaitlistForProviderDate(providerId, desiredDate, t0) {
  const $ = _c(10);
  const enabled = t0 === undefined ? true : t0;
  const hasWaitlist = useModule("WAITLIST");
  let t1;
  if ($[0] !== desiredDate || $[1] !== providerId) {
    t1 = waitlistQueryKeys.list(providerId ? [providerId] : undefined, desiredDate, activeWaitlistStatuses);
    $[0] = desiredDate;
    $[1] = providerId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== desiredDate || $[4] !== providerId) {
    t2 = () => listWaitlistEntries(providerId ? [providerId] : undefined, desiredDate, activeWaitlistStatuses);
    $[3] = desiredDate;
    $[4] = providerId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = hasWaitlist && enabled && !!providerId && !!desiredDate;
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 30000,
      refetchOnWindowFocus: true
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
}
export function useWaitlistForProvidersDate(providerIds, desiredDate, t0, locationIds, t1, matchProviderIds) {
  const $ = _c(25);
  const enabled = t0 === undefined ? true : t0;
  const statuses = t1 === undefined ? activeWaitlistStatuses : t1;
  const hasWaitlist = useModule("WAITLIST");
  let sortedIds;
  let sortedLocationIds;
  let sortedMatchIds;
  let t2;
  let t3;
  if ($[0] !== desiredDate || $[1] !== locationIds || $[2] !== matchProviderIds || $[3] !== providerIds || $[4] !== statuses) {
    sortedIds = providerIds?.length ? [...providerIds].sort() : undefined;
    sortedLocationIds = locationIds?.length ? [...locationIds].sort() : undefined;
    sortedMatchIds = matchProviderIds?.length ? Array.from(new Set(matchProviderIds)).sort() : undefined;
    t3 = useQuery;
    t2 = waitlistQueryKeys.list(sortedIds, desiredDate, statuses, undefined, sortedLocationIds, sortedMatchIds);
    $[0] = desiredDate;
    $[1] = locationIds;
    $[2] = matchProviderIds;
    $[3] = providerIds;
    $[4] = statuses;
    $[5] = sortedIds;
    $[6] = sortedLocationIds;
    $[7] = sortedMatchIds;
    $[8] = t2;
    $[9] = t3;
  } else {
    sortedIds = $[5];
    sortedLocationIds = $[6];
    sortedMatchIds = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  let t4;
  if ($[10] !== desiredDate || $[11] !== sortedIds || $[12] !== sortedLocationIds || $[13] !== sortedMatchIds || $[14] !== statuses) {
    t4 = () => listWaitlistEntries(sortedIds, desiredDate, statuses, undefined, sortedLocationIds, sortedMatchIds);
    $[10] = desiredDate;
    $[11] = sortedIds;
    $[12] = sortedLocationIds;
    $[13] = sortedMatchIds;
    $[14] = statuses;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = hasWaitlist && enabled && (providerIds === undefined || !!sortedIds?.length) && !!desiredDate;
  let t6;
  if ($[16] !== desiredDate || $[17] !== sortedLocationIds || $[18] !== sortedMatchIds) {
    t6 = (previous, previousQuery) => {
      const before = previousQuery?.queryKey[2];
      return sortedMatchIds && before?.matchProviderIds && before.desiredDate === desiredDate && before.locationIds?.join() === sortedLocationIds?.join() ? previous : undefined;
    };
    $[16] = desiredDate;
    $[17] = sortedLocationIds;
    $[18] = sortedMatchIds;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t2 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t7 = {
      queryKey: t2,
      queryFn: t4,
      enabled: t5,
      placeholderData: t6,
      staleTime: 30000,
      refetchOnWindowFocus: true
    };
    $[20] = t2;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t3(t7);
}
export function useWaitlistForDate(desiredDate, t0) {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  const hasWaitlist = useModule("WAITLIST");
  let t1;
  if ($[0] !== desiredDate) {
    t1 = waitlistQueryKeys.list(undefined, desiredDate, activeWaitlistStatuses);
    $[0] = desiredDate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== desiredDate) {
    t2 = () => listWaitlistEntries(undefined, desiredDate, activeWaitlistStatuses);
    $[2] = desiredDate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = hasWaitlist && enabled && !!desiredDate;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 30000,
      refetchOnWindowFocus: true
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
}

// --- Mutation hooks ---

export function useCreateWaitlistMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: waitlistQueryKeys.all
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp2(data) {
  return createWaitlistEntry(data);
}
export function useUpdateWaitlistMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: (entry, t1) => {
        const {
          data: data_0
        } = t1;
        if (!entry) {
          return;
        }
        queryClient.setQueryData(waitlistQueryKeys.detail(entry.id), entry);
        const requestChanged = data_0.assignments !== undefined || data_0.locationId !== undefined;
        const preferencesChanged = requestChanged || data_0.startDate !== undefined || data_0.endDate !== undefined;
        for (const [key, entries] of queryClient.getQueriesData({
          queryKey: waitlistQueryKeys.lists()
        })) {
          const filters = key[2] as ReturnType<typeof waitlistQueryKeys.list>[2];
          const {
            providerIds,
            locationIds,
            desiredDate,
            matchProviderIds
          } = filters;
          const matchesLocation = !locationIds?.length || entry.locationId === null || locationIds.includes(entry.locationId);
          const providersKnown = entry.assignments.every(_temp4);
          const matchesProviders = !providerIds?.length || !providersKnown || entry.assignments.some(assignment_0 => providerIds.includes(assignment_0.providerId));
          const matches = (filters.status?.length ? filters.status : activeWaitlistStatuses).includes(entry.status) && (!desiredDate || entry.startDate <= desiredDate && desiredDate <= entry.endDate) && matchesLocation && matchesProviders;
          const wasMember = entries?.some(item => item.id === entry.id);
          queryClient.setQueryData(key, current => current?.flatMap(item_0 => item_0.id !== entry.id ? [item_0] : matches ? [{
            ...entry,
            matchingProviderIds: item_0.matchingProviderIds
          }] : []));
          if (matches && (preferencesChanged || !wasMember && data_0.status !== undefined)) {
            queryClient.invalidateQueries({
              queryKey: key,
              exact: true,
              refetchType: requestChanged && (matchProviderIds?.length || providerIds?.length && !providersKnown) ? "active" : "none"
            });
          }
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp4(assignment) {
  return assignment.providerId;
}
function _temp3(t0) {
  const {
    waitlistId,
    data
  } = t0;
  return updateWaitlistEntry(waitlistId, data);
}
export function useDeleteWaitlistMutation() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp5,
      onSuccess: (_result, waitlistId_0) => {
        queryClient.setQueriesData({
          queryKey: waitlistQueryKeys.lists()
        }, entries => entries?.filter(entry => entry.id !== waitlistId_0));
        queryClient.removeQueries({
          queryKey: waitlistQueryKeys.detail(waitlistId_0),
          exact: true
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp5(waitlistId) {
  return deleteWaitlistEntry(waitlistId);
}